/* ============================================================
   CULIANS — PDP image lightbox (reference system)
   ============================================================
   Replaces Atelier's zoom dialog with PhotoSwipe 5 configured to
   match the reference interaction exactly:
   - Click a gallery image: it zooms FROM its position in the
     left gallery column to a full-page view (showHideAnimationType
     'zoom'), opening at 'fill' level — the image covers the page.
   - Click the zoomed image: toggles to 'fit' — image centered,
     not zoomed, black ground either side.
   - "Close" text top-right; thin white chevrons either side step
     through all images; ESC and vertical drag also close.
   PhotoSwipe core is loaded from cdnjs on first open (dynamic
   import), so the PDP pays nothing until a zoom happens.
   ============================================================ */
(function () {
  'use strict';

  var PSWP_URL = 'https://cdnjs.cloudflare.com/ajax/libs/photoswipe/5.4.4/photoswipe.esm.min.js';

  // Shopify's CDN minifier rewrites a literal `import(url)` into
  // `require(url)`, which does not exist in the browser, so the lightbox
  // silently failed on Dev/Live and Atelier's zoom dialog opened instead
  // (2026-09-08). Building the import through Function keeps it out of
  // the minifier's reach.
  var loadModule = new Function('u', 'return import(u)');

  // Solid triangles (reference). Color comes from currentColor; the state
  // class on the root flips it black/white with the ground.
  var ARROW_LEFT =
    '<svg class="culians-pswp-arrow-svg" width="5" height="8" viewBox="0 0 5 8" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M4.6 0.4 0.6 4 4.6 7.6 Z" fill="currentColor"/></svg>';
  var ARROW_RIGHT =
    '<svg class="culians-pswp-arrow-svg" width="5" height="8" viewBox="0 0 5 8" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M0.4 0.4 4.4 4 0.4 7.6 Z" fill="currentColor"/></svg>';

  function masterUrl(img) {
    // Shopify CDN: swap/force the width param up to the master size.
    var src = img.currentSrc || img.src;
    if (!src) return null;
    if (src.indexOf('width=') !== -1) return src.replace(/width=\d+/, 'width=3840');
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=3840';
  }

  function collectSlides(gallery) {
    var imgs = [];
    gallery.querySelectorAll('.product-media-container img').forEach(function (img) {
      // Skip duplicates (thumbnails etc.) by container — one img per container.
      imgs.push(img);
    });
    return imgs.map(function (img) {
      var w = parseInt(img.getAttribute('width'), 10) || img.naturalWidth || 3840;
      var h = parseInt(img.getAttribute('height'), 10) || img.naturalHeight || 4800;
      return { src: masterUrl(img), width: w, height: h, alt: img.alt || '', element: img };
    });
  }

  // Atelier wires its own zoom dialog declaratively (on:click="#zoom-dialog…")
  // on the phone slides and handles it at document level, ahead of any
  // listener on the gallery. Removing the attribute is the only reliable
  // way to keep that dialog closed (2026-09-09).
  function disarmAtelierZoom(gallery) {
    gallery.querySelectorAll('[on\\:click*="zoom-dialog"]').forEach(function (el) {
      el.removeAttribute('on:click');
    });
  }

  function init() {
    var gallery = document.querySelector('media-gallery, .media-gallery__grid');
    if (!gallery) return;
    disarmAtelierZoom(gallery);
    if (gallery.hasAttribute('data-culians-lightbox-init')) return;
    gallery.setAttribute('data-culians-lightbox-init', '');
    new MutationObserver(function () { disarmAtelierZoom(gallery); })
      .observe(gallery, { childList: true, subtree: true });

    // Preload the full-resolution file the moment the pointer enters a
    // gallery image, so the zoom opens onto a sharp image instead of a
    // placeholder that later blur-pops into focus.
    gallery.addEventListener('pointerenter', function (e) {
      var container = e.target.closest && e.target.closest('.product-media-container');
      if (!container) return;
      var img = container.querySelector('img');
      if (!img || img.hasAttribute('data-culians-preloaded')) return;
      img.setAttribute('data-culians-preloaded', '');
      var full = new Image();
      full.src = masterUrl(img);
    }, true);

    // Capture-phase listener so Atelier's own zoom dialog never opens.
    gallery.addEventListener(
      'click',
      function (e) {
        var container = e.target.closest('.product-media-container');
        if (!container) return;
        var img = container.querySelector('img');
        if (!img) return;
        e.preventDefault();
        e.stopPropagation();

        var slides = collectSlides(gallery);
        var index = slides.findIndex(function (s) { return s.element === img; });
        if (index < 0) index = 0;

        loadModule(PSWP_URL).then(function (mod) {
          var PhotoSwipe = mod.default;
          var touch = window.matchMedia('(pointer: coarse)').matches;
          var pswp = new PhotoSwipe({
            dataSource: slides.map(function (s) {
              // No thumbCropped: the gallery image is uncropped, so the
              // open animation is one uniform scale from the gallery to
              // the fill position — the viewport crops naturally. (The
              // cropped variant animated a clip box: height-then-width,
              // visibly two-phase.)
              return { src: s.src, width: s.width, height: s.height, alt: s.alt, element: s.element };
            }),
            index: index,
            mainClass: 'culians-pswp',
            bgOpacity: 1,
            showHideAnimationType: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'none' : 'zoom',
            showAnimationDuration: 500,
            hideAnimationDuration: 400,
            easing: 'cubic-bezier(0.4, 0, 0.22, 1)',
            initialZoomLevel: function (zoomLevels) {
              return mode === 'fit' ? zoomLevels.fit : zoomLevels.fill;
            },
            secondaryZoomLevel: 'fit',
            // Desktop: two states only (fill / fit), no free zoom. Touch
            // (2026-09-13, Suzan: "zoom on mobile is not working"): the
            // 'fill' cap made every pinch spring straight back, so the
            // lightbox never actually zoomed. Pinch now goes up to 3× the
            // fill level, never past the master file's native pixels.
            maxZoomLevel: touch
              ? function (zoomLevels) { return Math.min(1, zoomLevels.fill * 3); }
              : 'fill',
            pinchToClose: false,
            imageClickAction: function () { toggleMode(); },
            tapAction: function () { toggleMode(); },
            doubleTapAction: false,
            escAction: function () { closeWithRhythm(); },
            counter: false,
            zoom: false,
            close: false,
            arrowPrevSVG: ARROW_LEFT,
            arrowNextSVG: ARROW_RIGHT,
            arrowPrevTitle: 'Previous image',
            arrowNextTitle: 'Next image',
            preloadFirstSlide: true,
            loop: false   /* 2026-09-09: arrows reflect a real previous/next image */
          });

          // Zoom-from-thumbnail: point PhotoSwipe at the clicked
          // gallery image so the open/close animation originates there.
          pswp.addFilter('thumbEl', function (thumbEl, data) {
            return data.element || thumbEl;
          });
          pswp.addFilter('placeholderSrc', function (placeholderSrc, slide) {
            var el = slide.data.element;
            return el ? (el.currentSrc || el.src) : placeholderSrc;
          });

          // "Close" as quiet text, top right — same register as the drawers.
          // Closing mirrors opening: PhotoSwipe only zoom-animates the
          // close from the full (initial) state, so from the centered
          // state we first glide back to full, then let the close glide
          // the image down into the gallery — one continuous reverse.
          function closeWithRhythm() {
            if (mode === 'fit') {
              // Centered state closes DIRECTLY: a quiet fade back to the
              // PDP (no detour through the full-page state).
              pswp.options.showHideAnimationType = 'fade';
            }
            pswp.close();
          }
          pswp.on('uiRegister', function () {
            pswp.ui.registerElement({
              name: 'culians-close',
              order: 20,
              isButton: true,
              tagName: 'button',
              html: 'Close',
              onClick: function () { closeWithRhythm(); }
            });
          });

          // Viewing mode is a USER CHOICE (best practice): it only
          // changes when the viewer clicks the image, and it persists
          // across prev/next — centered stays centered, zoomed stays
          // zoomed. The CSS state class merely mirrors the mode.
          var mode = 'fill';

          function applyModeClass() {
            if (pswp.element) pswp.element.classList.toggle('culians-pswp--fit', mode === 'fit');
          }

          function levelFor(slide) {
            return mode === 'fit' ? slide.zoomLevels.fit : slide.zoomLevels.fill;
          }

          function centerPoint() {
            return { x: pswp.viewportSize.x / 2, y: pswp.viewportSize.y / 2 };
          }

          function toggleMode() {
            var slide = pswp.currSlide;
            if (!slide) return;
            mode = mode === 'fit' ? 'fill' : 'fit';
            if (mode === 'fit') {
              // Zooming out: black ground appears immediately, so the
              // area the image vacates is already black.
              applyModeClass();
            } else {
              // Zooming in: keep the black ground until the image has
              // covered the page, then flip to white invisibly. (Flipping
              // early spread a white ground behind the still-zooming
              // image.)
              setTimeout(applyModeClass, 350);
            }
            pswp.zoomTo(levelFor(slide), centerPoint(), 333);
            refreshNeighbors();
          }

          // Re-layout the two neighboring (preloaded) slides at the
          // current mode's zoom level, so stepping shows them correctly
          // from the first frame.
          function refreshNeighbors() {
            if (!pswp.mainScroll || !pswp.mainScroll.itemHolders) return;
            pswp.mainScroll.itemHolders.forEach(function (holder) {
              var sl = holder && holder.slide;
              if (sl && sl !== pswp.currSlide && sl.zoomLevels) {
                sl.zoomAndPanToInitial();
                sl.applyCurrentZoomPan();
              }
            });
          }

          pswp.on('afterInit', applyModeClass);


          // Prev/next (reference): in the FULL view the switch is an
          // instant cut. In the CENTERED view the transition happens
          // INSIDE the picture frame: the frame stays fixed on the black
          // ground and the images slide horizontally within it, clipped
          // by its edges. (All Culians images share the 4:5 ratio, so
          // the frame is identical from slide to slide.)
          var stepping = false;
          function fadeSwap(delta) {
            if (mode === 'fill') {
              pswp.mainScroll.moveIndexBy(delta, false);
              return;
            }
            if (stepping) return;
            var outgoing = pswp.currSlide;
            if (!outgoing || !outgoing.zoomLevels) { pswp.mainScroll.moveIndexBy(delta, false); return; }
            stepping = true;

            var vw = pswp.viewportSize;
            var z = outgoing.zoomLevels.fit;
            var fw = Math.round(outgoing.width * z);
            var fh = Math.round(outgoing.height * z);
            var fx = Math.round((vw.x - fw) / 2);
            var fy = Math.round((vw.y - fh) / 2);
            var outSrc = (outgoing.data.element && outgoing.data.element.currentSrc) || outgoing.data.src;

            // Switch instantly underneath, hidden.
            var wrap = pswp.element.querySelector('.pswp__container');
            pswp.mainScroll.moveIndexBy(delta, false);
            var incoming = pswp.currSlide;
            var inSrc = (incoming.data.element && incoming.data.element.currentSrc) || incoming.data.src;
            if (wrap) wrap.style.visibility = 'hidden';

            // Fixed, clipped frame with a two-image track sliding inside.
            var frame = document.createElement('div');
            frame.style.cssText = 'position:absolute;overflow:hidden;z-index:5;' +
              'left:' + fx + 'px;top:' + fy + 'px;width:' + fw + 'px;height:' + fh + 'px;';
            var track = document.createElement('div');
            track.style.cssText = 'position:absolute;top:0;left:0;height:100%;width:' + (fw * 2) + 'px;display:flex;will-change:transform;';
            function mkImg(src) {
              var im = document.createElement('img');
              im.src = src;
              im.style.cssText = 'width:' + fw + 'px;height:' + fh + 'px;object-fit:contain;flex:0 0 auto;';
              return im;
            }
            var fromX, toX;
            if (delta > 0) {
              track.appendChild(mkImg(outSrc));
              track.appendChild(mkImg(inSrc));
              fromX = 0; toX = -fw;
            } else {
              track.appendChild(mkImg(inSrc));
              track.appendChild(mkImg(outSrc));
              fromX = -fw; toX = 0;
            }
            frame.appendChild(track);
            pswp.element.appendChild(frame);

            var t0 = performance.now();
            var D = 450;
            (function frameStep(now) {
              var t = Math.min(1, ((now || performance.now()) - t0) / D);
              var e = 1 - Math.pow(1 - t, 3); // ease-out cubic
              track.style.transform = 'translate3d(' + (fromX + (toX - fromX) * e) + 'px, 0, 0)';
              if (t < 1) {
                requestAnimationFrame(frameStep);
              } else {
                if (wrap) wrap.style.visibility = '';
                frame.remove();
                stepping = false;
              }
            })(t0);
          }

          pswp.on('afterInit', function () {
            if (!pswp.element) return;
            pswp.element.addEventListener('click', function (ev) {
              var btn = ev.target.closest('.pswp__button--arrow');
              if (!btn) return;
              ev.preventDefault();
              ev.stopPropagation();
              fadeSwap(btn.classList.contains('pswp__button--arrow--prev') ? -1 : 1);
            }, true);
          });

          // Each newly shown slide arrives already in the chosen mode —
          // snapped instantly before paint, so there is no fit-to-fill
          // jolt when stepping through images.
          pswp.on('change', function () {
            var enforce = function () {
              // Never touch the slide while the OPEN animation is
              // running — zoomTo would cancel it mid-flight.
              if (pswp.opener && !pswp.opener.isOpen) return;
              var slide = pswp.currSlide;
              if (!slide || !slide.zoomLevels) return;
              var target = levelFor(slide);
              if (Math.abs(slide.currZoomLevel - target) > 0.001) {
                pswp.zoomTo(target, centerPoint(), 0);
              }
              applyModeClass();
            };
            enforce();
            requestAnimationFrame(enforce);
          });

          // Lock page scroll (and hide the scrollbar) while open —
          // same discipline as the drawers.
          document.body.classList.add('culians-lightbox-open');
          pswp.on('destroy', function () {
            document.body.classList.remove('culians-lightbox-open');
          });

          // Closing returns the page to the image that was open (native
          // photo-viewer behaviour, 2026-09-09). On the phone carousel the
          // slide is selected as the close begins, so the zoom-out lands on
          // it rather than on an off-screen slide.
          pswp.on('close', function () {
            var sc = gallery.querySelector('slideshow-component');
            if (!sc || typeof sc.select !== 'function') return;
            try { sc.select(pswp.currIndex, undefined, { animate: false }); } catch (e) {}
          });

          pswp.init();
        });
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
